/**
 * HTTP-клиент.
 *
 * Отвечает за три вещи, которые иначе расползаются по фичам:
 *  1. заголовок авторизации Telegram на каждом запросе;
 *  2. Idempotency-Key на каждой мутации, переживающий ретраи (ADR-0004);
 *  3. единый разбор ошибок в ApiError со стабильным кодом.
 */

import { getInitData } from '@/shared/telegram'

const BASE = '/api'
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const RETRY_DELAYS_MS = [400, 1200]

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id?: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.request_id
  }

  /** Ошибка сети или сервера: повтор тем же ключом безопасен и осмыслен. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500 || this.code === 'idempotency_in_progress'
  }

  get isAuth(): boolean {
    return this.status === 401
  }
}

export function newIdempotencyKey(): string {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export interface RequestOptions {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Переиспользуемый ключ: передайте свой, чтобы повтор не создал дубль. */
  idempotencyKey?: string
  signal?: AbortSignal
  familyId?: number
  /** Сколько раз повторить при сетевой ошибке. Мутации безопасны за счёт ключа. */
  retries?: number
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const data = (await response.json()) as { error?: ApiErrorBody }
    if (data.error) return new ApiError(response.status, data.error)
  } catch {
    /* тело не json — ниже соберём общую ошибку */
  }
  return new ApiError(response.status, {
    code: response.status === 404 ? 'not_found' : 'http_error',
    message: 'Не удалось связаться с сервером',
  })
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const isMutation = MUTATING.has(method)
  const headers: Record<string, string> = {
    Authorization: `tma ${getInitData()}`,
  }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.familyId) headers['X-Family-Id'] = String(options.familyId)
  // ключ создаётся один раз и переиспользуется всеми повторами этого вызова
  if (isMutation) headers['Idempotency-Key'] = options.idempotencyKey ?? newIdempotencyKey()

  const url = buildUrl(path, options.query)
  const maxAttempts = 1 + (options.retries ?? (isMutation ? RETRY_DELAYS_MS.length : 1))
  let lastError: ApiError | undefined

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1] ?? 1200)
    try {
      const response = await fetch(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      })

      if (response.ok) {
        if (response.status === 204) return undefined as T
        return (await response.json()) as T
      }

      const error = await parseError(response)
      if (!error.isRetryable) throw error
      lastError = error
    } catch (error) {
      if (error instanceof ApiError) {
        if (!error.isRetryable) throw error
        lastError = error
        continue
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      // сеть отвалилась — статус 0, повторяем тем же ключом
      lastError = new ApiError(0, {
        code: 'network_error',
        message: 'Нет связи. Попробуем ещё раз',
      })
    }
  }

  throw lastError ?? new ApiError(0, { code: 'network_error', message: 'Нет связи' })
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
}
